import { newId } from '@/lib/ids';
import { allocatedHours, breakdown, type AllocationDraft } from '@/lib/diary';
import { hours as fmtHours } from '@/lib/format';
import type { TimeCategory } from '@/types/models';

const STEP = 0.25;

interface Props {
  categories: TimeCategory[];
  allocations: AllocationDraft[];
  scheduledHours: number;
  tolerance: number;
  /** A category set from elsewhere (productive = engine hours on an hour
   *  meter, SAF-84): shown, not edited. */
  fixedCategoryId?: string;
  fixedHint?: string;
  onChange: (next: AllocationDraft[]) => void;
}

/**
 * Every category is a row with a stepper. The running bar underneath shows
 * the shift filling up — green when it reconciles, amber when time is
 * unaccounted for, red when the shift has been over-allocated. A supervisor
 * should be able to tell at a glance, without reading a number.
 */
export function TimeAllocation({
  categories, allocations, scheduledHours, tolerance, fixedCategoryId, fixedHint, onChange,
}: Props) {
  const total = allocatedHours(allocations);
  const variance = Math.round((total - scheduledHours) * 100) / 100;
  const bd = breakdown(allocations, categories);

  // Over is over: the tolerance only forgives time left unbooked (0049).
  const state = variance > 0 ? 'over' : variance < -tolerance ? 'under' : 'ok';

  function hoursFor(categoryId: string): number {
    return allocations.find((a) => a.time_category_id === categoryId)?.hours ?? 0;
  }

  function setHours(categoryId: string, value: number) {
    const clean = Math.max(0, Math.min(24, Math.round(value * 100) / 100));
    const existing = allocations.find((a) => a.time_category_id === categoryId);

    if (clean === 0) {
      onChange(allocations.filter((a) => a.time_category_id !== categoryId));
      return;
    }
    if (existing) {
      onChange(allocations.map((a) =>
        a.time_category_id === categoryId ? { ...a, hours: clean } : a));
      return;
    }
    onChange([
      ...allocations,
      { id: newId(), time_category_id: categoryId, hours: clean, note: null },
    ]);
  }

  const segments: { key: keyof typeof bd; label: string; colour: string }[] = [
    { key: 'productive', label: 'Productive', colour: 'var(--green)' },
    { key: 'standby', label: 'Standby', colour: 'var(--text-secondary)' },
    { key: 'unplanned', label: 'Breakdown', colour: 'var(--red)' },
    { key: 'planned', label: 'Planned', colour: 'var(--blue)' },
    { key: 'weather', label: 'Weather', colour: 'var(--orange)' },
  ];

  const denominator = Math.max(total, scheduledHours) || 1;

  return (
    <>
      {categories.map((c) => {
        const h = hoursFor(c.id);
        const fixed = c.id === fixedCategoryId;
        return (
          <div className={`alloc${h === 0 ? ' alloc--zero' : ''}`} key={c.id}>
            <div className="alloc__name">
              <span className="alloc__swatch" style={{ background: c.colour ?? 'var(--text-secondary)' }} />
              <div style={{ minWidth: 0 }}>
                <div className="alloc__label">{c.name}</div>
                {c.is_breakdown && <div className="alloc__hint">Log a breakdown for this time</div>}
                {fixed && fixedHint && <div className="alloc__hint">{fixedHint}</div>}
              </div>
            </div>

            <div className="stepper">
              <button
                type="button"
                className="stepper__btn"
                aria-label={`Less ${c.name}`}
                disabled={fixed || h <= 0}
                onClick={() => setHours(c.id, h - STEP)}
              >
                −
              </button>
              <input
                className="stepper__value"
                type="number"
                inputMode="decimal"
                step={STEP}
                min={0}
                max={24}
                value={h === 0 ? '' : h}
                placeholder="0"
                readOnly={fixed}
                onChange={(e) => setHours(c.id, e.target.value === '' ? 0 : Number(e.target.value))}
              />
              <button
                type="button"
                className="stepper__btn"
                aria-label={`More ${c.name}`}
                disabled={fixed}
                onClick={() => setHours(c.id, h + STEP)}
              >
                +
              </button>
            </div>
          </div>
        );
      })}

      <div className="reconcile">
        <div className="reconcile__bar">
          {segments.map((s) => {
            const value = bd[s.key];
            if (!value) return null;
            return (
              <div
                key={s.key}
                className="reconcile__seg"
                style={{ width: `${(value / denominator) * 100}%`, background: s.colour }}
                title={`${s.label} ${fmtHours(value)} h`}
              />
            );
          })}
        </div>

        <div className="reconcile__legend">
          {segments.filter((s) => bd[s.key] > 0).map((s) => (
            <span className="reconcile__item" key={s.key}>
              <span className="reconcile__dot" style={{ background: s.colour }} />
              {s.label} {fmtHours(bd[s.key])} h
            </span>
          ))}
        </div>

        <div className={`reconcile__total reconcile__total--${state}`}>
          <strong>{fmtHours(total)} h</strong>
          <span className="small">of {fmtHours(scheduledHours)} h scheduled</span>
          {state === 'over' && <span className="small">· {fmtHours(variance)} h over the shift</span>}
          {state === 'under' && <span className="small">· {fmtHours(Math.abs(variance))} h unaccounted for</span>}
          {state === 'ok' && <span className="small">· reconciled</span>}
        </div>
      </div>
    </>
  );
}
